/**
 * The token grammar of `settings.brand_selections`, mirrored from the API's
 * `App\Services\Brand\BrandSelectionToken`.
 *
 *   siding:brand:12
 *   siding:line:40@double-4-dutchlap
 *   siding:product:88@double-4-dutchlap
 *   paint:brand:3
 *   paint:product:412
 *   tile:product:9@600x600.rectified          a library with two axes
 *
 * Three shapes are read forever and none may be "cleaned up":
 *
 *  - the canonical three-segment form above;
 *  - `color` wherever `product` belongs — what the third level was called while
 *    the catalogue was two hand-built libraries;
 *  - the two-segment form ("paint:412"), which is what these picks looked like
 *    while they lived inside `settings.colors`.
 *
 * The axis part carries VALUE SLUGS, not `axis=value` pairs: a value slug is
 * unique within its category, so the axis it belongs to is a lookup rather than
 * something the token has to spell. Values are separated by a dot because a
 * token travels in a query string, where `+` decodes to a space.
 *
 * The CATEGORY is deliberately not a union type here. Which libraries exist is
 * something the client asks the API (`/v1/catalog/categories`) — hardcoding the
 * list is what made adding one a release on three platforms.
 */
export type BrandLevel = 'brand' | 'line' | 'product';

/** Same ceiling the API enforces on `data.attributes.brand_selections`. */
export const MAX_BRAND_SELECTIONS = 10;

export interface ParsedBrandToken {
  token: string;
  category: string;
  level: BrandLevel;
  id: string;
  /** Empty when the pick names no axis value. */
  axisValueSlugs: string[];
}

const LEVELS: BrandLevel[] = ['brand', 'line', 'product'];
const AXIS_SEPARATOR = '.';

/**
 * Prefixes that belong to another axis and can never name a library.
 *
 * `settings.colors` mixes three forms in one array, and a palette reference has
 * the same two-segment shape as a pre-catalogue product pick ("palette:11" vs
 * "paint:412"). With the category now an open string — which is what lets a new
 * library ship without a client release — the two are indistinguishable unless
 * the colour axis keeps its word reserved.
 */
const RESERVED_CATEGORIES = ['palette'];

export function buildBrandToken(
  category: string,
  level: BrandLevel,
  id: string,
  axisValueSlugs: string[] = []
): string {
  const base = `${category}:${level}:${id}`;
  // Sorted so one selection has one canonical spelling: the order of axis
  // values carries no meaning, and an unsorted one would make the same pick
  // look like two different tokens to `picks.includes(token)`.
  const slugs = [...new Set(axisValueSlugs.filter(Boolean))].sort();

  return slugs.length > 0 ? `${base}@${slugs.join(AXIS_SEPARATOR)}` : base;
}

export function parseBrandToken(token: string): ParsedBrandToken | null {
  const [body, axisPart] = token.split('@');
  const parts = (body ?? '').split(':');

  // Two segments is the pre-catalogue shape ("paint:412"), always a product.
  const normalized =
    parts.length === 2 ? [parts[0]!, 'product', parts[1]!] : parts;

  if (normalized.length !== 3) return null;

  const [category, rawLevel, id] = normalized as [string, string, string];
  const level = rawLevel === 'color' ? 'product' : rawLevel;

  if (!category || RESERVED_CATEGORIES.includes(category)) return null;
  if (!LEVELS.includes(level as BrandLevel)) return null;
  if (!id || !/^\d+$/.test(id)) return null;

  const axisValueSlugs =
    axisPart === undefined
      ? []
      : axisPart.split(AXIS_SEPARATOR).filter((slug) => slug !== '');

  // A trailing "@" or "@." names no value and is not a token.
  if (axisPart !== undefined && axisValueSlugs.length === 0) return null;

  return {
    token,
    category,
    level: level as BrandLevel,
    id,
    axisValueSlugs,
  };
}

/**
 * Split a stored settings snapshot into the two axes it means today.
 *
 * Every render made before Brands became its own setting kept its product picks
 * inside `colors`, because that is where the picker lived. Loading such a render
 * into the form must put those picks on the Brands row — leaving them in Colors
 * would show the user a Colors tab that no longer carries them, and
 * re-submitting would keep them on the old axis forever.
 *
 * Tokens are rewritten into the canonical grammar, which is the only shape
 * `data.attributes.brand_selections` is written in.
 */
export function migrateLegacyProductPicks(
  colors: string[] | null | undefined,
  brandSelections: string[] | null | undefined
): { colors: string[] | null; brand_selections: string[] | null } {
  const existing = brandSelections ?? [];
  const remaining: string[] = [];
  const migrated: string[] = [];

  for (const entry of colors ?? []) {
    const parsed = parseBrandToken(entry);

    if (parsed === null) {
      remaining.push(entry);
      continue;
    }

    migrated.push(
      buildBrandToken(
        parsed.category,
        parsed.level,
        parsed.id,
        parsed.axisValueSlugs
      )
    );
  }

  // One pick is one token (a render that already carried the pick on both
  // axes must not list it twice), and the list stays under the API's ceiling:
  // a migrated snapshot past it was refused with a 422 on the next Generate.
  // Existing Brands picks come first, so the overflow dropped is the oldest
  // legacy colour-row picks.
  const selections = [...new Set([...existing, ...migrated])].slice(
    0,
    MAX_BRAND_SELECTIONS
  );

  return {
    colors: remaining.length > 0 ? remaining : null,
    brand_selections: selections.length > 0 ? selections : null,
  };
}
